/**
 * PATH: packages/email-hub/src/feature/reader-header/adapters/useReaderCoreVerbs.ts
 * FILE_ID: EMAILHUB-READERHEADER-ADAPTER-COREVERBS-001
 * PURPOSE: Bind the Phase 2 action runtime to the proven email transports for
 *   every core verb: Done, Archive, Delete, Junk, Read/Unread, Star, Snooze.
 * ROLE IN ARCHITECTURE: The one transport-wiring layer under
 *   useReaderHeaderCommands — both surfaces share identical verb semantics.
 * MAIN EXPORTS: useReaderCoreVerbs.
 * NON-RESPONSIBILITIES: No UI, no menu assembly, no API code (transports are
 *   useEmailActions); the runner owns timing/undo/errors/telemetry.
 * NOTES FOR FUTURE AI: Terminal verbs MUST go through runTerminal so the
 *   host's beginRemoval restore closure rides the runner rollback (kills the
 *   silent Archive/Delete failure). Never toast here — the runner does,
 *   EXCEPT `autoTrash`, which needs the dynamic binned-count/match-value
 *   copy `createAutoJunkRule` returns — same exception useJunkActions.ts
 *   (the legacy JunkButton/CardJunkControl transport) makes for the same
 *   reason. Do not add a second reason to toast inline here.
 */
import { useToast } from '../../../_internal/components/Toast'
import { useEmailActions } from '../../hooks/useEmails'
import { formatResurfaceTime } from '../../lib/resurfaceTimes'
import type { JunkMatchType } from '../../hooks/useJunkActions'
import { getReaderAction } from '../actions/registry'
import type { ReaderActionId } from '../model/types'
import type {
  ReaderActionRunner,
  ReaderRunContext,
} from '../runtime/useReaderActionRunner'
import type { ReaderHeaderHostBindings } from './types'

export interface ReaderCoreVerbs {
  done: () => void
  archive: () => void
  del: () => void
  junk: () => void
  /** Arm a retro+prospective auto-junk rule (D269 D1) — reaches the SAME
   *  `createAutoJunkRule` transport the legacy JunkButton/CardJunkControl
   *  use, shares the 'junk' busyKey (mutually exclusive with junkNow), and
   *  is terminal for THIS email like junk/archive/delete/done. */
  autoTrash: (matchType: JunkMatchType) => void
  snoozeSelect: (iso: string) => void
  toggleRead: () => void
  toggleStar: () => void
  /** Deferred-spinner state per verb (true only past the 400ms gate). */
  spinnerBusy: (busyKey: string) => boolean
}

/**
 * Wire the shared runner to the proven mutation hooks for the core verbs.
 *
 * Inputs: host bindings (email + terminal seam + patch seam) and the ONE
 * runner instance the adapter created (shared with DecisionActions so the
 * decision busyKey is symmetric). Output: plain verb closures for the
 * command bar / More menu. Side effects: none at assembly; each closure
 * fires exactly one runner.run. Failure modes: the runner classifies and
 * toasts every rejection — closures never throw.
 */
export function useReaderCoreVerbs(
  bindings: ReaderHeaderHostBindings,
  runner: ReaderActionRunner,
): ReaderCoreVerbs {
  const { email, terminal, kanban } = bindings
  const {
    archive: archiveEmail,
    unarchive,
    bulkAction,
    toggleStar: toggleStarApi,
    createAutoJunkRule,
  } = useEmailActions()
  const { toast } = useToast()

  const runTerminal = (
    id: ReaderActionId,
    execute: (ctx: ReaderRunContext) => Promise<unknown>,
    undoExecute: (ctx: ReaderRunContext) => Promise<unknown>,
    successToast: string,
  ) => {
    void runner.run({
      action: getReaderAction(id),
      execute,
      terminal: { remove: () => terminal.beginRemoval() },
      undo: { execute: undoExecute },
      successToast,
    })
  }

  /** Optimistic local flag flip with exact rollback (read/star toggles). */
  const runFlagToggle = (
    id: ReaderActionId,
    patch: Partial<typeof email>,
    revert: Partial<typeof email>,
    execute: (ctx: ReaderRunContext) => Promise<unknown>,
  ) => {
    void runner.run({
      action: getReaderAction(id),
      optimistic: {
        apply: () => kanban.onPatchEmail?.(email.id, patch),
        rollback: () => kanban.onPatchEmail?.(email.id, revert),
      },
      execute,
    })
  }

  const prevState = {
    folder: email.folder,
    isRead: email.is_read,
    isStarred: email.is_starred,
  }

  return {
    done: () =>
      runTerminal(
        'done',
        () => archiveEmail(email.id, { ...prevState, complete: true }),
        () => unarchive(email.id, { isRead: email.is_read }),
        'Done — archived and completed linked work',
      ),
    archive: () =>
      runTerminal(
        'archive',
        () => archiveEmail(email.id, prevState),
        () => unarchive(email.id, { isRead: email.is_read }),
        'Archived',
      ),
    del: () =>
      runTerminal(
        'delete',
        () => bulkAction([email.id], 'delete'),
        () => bulkAction([email.id], 'restore'),
        'Moved to bin',
      ),
    junk: () =>
      runTerminal(
        'junk',
        () => bulkAction([email.id], 'junk'),
        () => bulkAction([email.id], 'restore'),
        'Junked — moved to bin',
      ),
    // No `undo` spec: createAutoJunkRule's reversal is "Undo in Tags & Rules"
    // (a settings-surface action, not an inverse transport this runner can
    // hold) — the legacy JunkButton/CardJunkControl never offer an inline
    // Undo for armRule either. Toast text needs the response's real
    // binned_count/match_value, so it fires inside `execute` and the run
    // spec omits `successToast` (the runner would double-toast otherwise).
    autoTrash: (matchType: JunkMatchType) =>
      void runner.run({
        action: getReaderAction('junk'),
        terminal: { remove: () => terminal.beginRemoval() },
        execute: async () => {
          const res = await createAutoJunkRule(email.id, matchType)
          const n = res?.binned_count ?? 0
          toast(
            `Junk rule armed for ${res?.rule?.match_value ?? 'this sender'} - binned ${n} email${
              n === 1 ? '' : 's'
            }. Undo in Tags & Rules.`,
            'success',
          )
          return res
        },
      }),
    snoozeSelect: (iso: string) =>
      runTerminal(
        'snooze',
        () => bulkAction([email.id], 'snooze', { snoozed_until: iso }),
        () => bulkAction([email.id], 'unsnooze'),
        `Snoozed until ${formatResurfaceTime(iso)}`,
      ),
    toggleRead: () =>
      runFlagToggle(
        'mark_read',
        { is_read: !email.is_read },
        { is_read: email.is_read },
        () =>
          bulkAction([email.id], email.is_read ? 'mark_unread' : 'mark_read'),
      ),
    toggleStar: () =>
      runFlagToggle(
        'star',
        { is_starred: !email.is_starred },
        { is_starred: email.is_starred },
        () => toggleStarApi(email.id, email.is_starred),
      ),
    spinnerBusy: (busyKey: string) => {
      const phase = runner.phaseFor(busyKey)
      return phase === 'spinner' || phase === 'still-working'
    },
  }
}
